import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Mail, MapPin, MessageCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useSite } from "../lib/site";
import Reveal from "../components/Reveal";

export default function Contact() {
  const { settings } = useSite();
  const [page, setPage] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useSeo({ title: "Kontak — Heri Sensei", description: "Hubungi Heri Sensei melalui formulir, email, WhatsApp, atau Instagram.", path: "/kontak" });

  useEffect(() => {
    api.get("/content/contact").then((r) => setPage(r.data)).catch(() => setPage({}));
  }, []);

  const c = page || {};
  const contact = settings.contact || {};
  const socials = settings.socials || {};
  const wa = contact.whatsapp ? contact.whatsapp.replace(/[^0-9+]/g, "") : "";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      setDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-14 md:py-20">
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">{c.eyebrow || "Kontak"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4 leading-tight text-ink">{c.title || "Mari terhubung."}</h1>
        <p className="text-sub text-lg leading-relaxed mt-5">{c.intro}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
        <div className="lg:col-span-5 space-y-4">
          {contact.email && (
            <a href={`mailto:${contact.email}`} data-testid="contact-email-link" className="flex items-center gap-4 bg-surface border border-line rounded-lg p-5 hover:border-enji transition-colors">
              <span className="w-11 h-11 rounded-full bg-enji/10 text-enji flex items-center justify-center shrink-0"><Mail size={18} /></span>
              <span>
                <span className="block text-[13px] text-faint">Email</span>
                <span className="block font-semibold text-ink mt-0.5">{contact.email}</span>
              </span>
            </a>
          )}
          {wa && (
            <a href={`https://wa.me/${wa.replace("+", "")}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-4 bg-surface border border-line rounded-lg p-5 hover:border-enji transition-colors">
              <span className="w-11 h-11 rounded-full bg-moss/15 text-moss flex items-center justify-center shrink-0"><MessageCircle size={18} /></span>
              <span>
                <span className="block text-[13px] text-faint">WhatsApp</span>
                <span className="block font-semibold text-ink mt-0.5">{contact.whatsapp}</span>
              </span>
            </a>
          )}
          {socials.instagram && (
            <a href={socials.instagram.startsWith("http") ? socials.instagram : `https://instagram.com/${socials.instagram.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="flex items-center gap-4 bg-surface border border-line rounded-lg p-5 hover:border-enji transition-colors">
              <span className="w-11 h-11 rounded-full bg-indigo/10 text-indigo flex items-center justify-center shrink-0 font-serif">@</span>
              <span>
                <span className="block text-[13px] text-faint">Instagram</span>
                <span className="block font-semibold text-ink mt-0.5">{socials.instagram}</span>
              </span>
            </a>
          )}
          {contact.address && (
            <div className="flex items-center gap-4 bg-surface border border-line rounded-lg p-5">
              <span className="w-11 h-11 rounded-full bg-surface-muted text-sub flex items-center justify-center shrink-0"><MapPin size={18} /></span>
              <span>
                <span className="block text-[13px] text-faint">Alamat</span>
                <span className="block text-ink mt-0.5">{contact.address}</span>
              </span>
            </div>
          )}
          {!contact.email && !wa && !socials.instagram && !contact.address && (
            <p className="text-sub text-sm bg-surface-muted rounded-lg p-5">Kanal kontak akan ditampilkan setelah tersedia.</p>
          )}
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <div className="bg-surface border border-line rounded-xl p-6 sm:p-8" data-testid="contact-form-card">
              <h2 className="font-serif text-2xl text-ink">{c.form_title || "Kirim Pesan"}</h2>
              {done ? (
                <div data-testid="contact-success" className="mt-6 flex gap-3 bg-moss/10 border border-moss/30 rounded-lg p-5">
                  <CheckCircle2 className="text-moss shrink-0 mt-0.5" size={20} />
                  <p className="text-ink text-[15px] leading-relaxed">{done.message}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="ct-name" className="field-label">Nama *</label>
                    <input id="ct-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className="field-input" placeholder="Nama Anda" />
                  </div>
                  <div>
                    <label htmlFor="ct-email" className="field-label">Email *</label>
                    <input id="ct-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className="field-input" placeholder="nama@email.com" />
                  </div>
                  <div>
                    <label htmlFor="ct-msg" className="field-label">Pesan *</label>
                    <textarea id="ct-msg" data-testid="contact-message-input" required value={form.message} onChange={set("message")} className="field-input" placeholder="Tulis pesan Anda…" />
                  </div>
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hp-field" aria-hidden="true" value={form.website} onChange={set("website")} />
                  {error && <p data-testid="contact-error" role="alert" className="text-sm text-enji">{error}</p>}
                  <button type="submit" data-testid="contact-submit-button" disabled={loading} className="btn-primary w-full sm:w-auto">
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {c.submit_label || "Kirim Pesan"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
